import { initClient } from "rwsdk/client";
import * as Sentry from "@sentry/react";
import { initAnalytics } from "@/lib/analytics/client";
import { reloadOnPreloadError } from "@/lib/preload-error-reload";
import { keepServerHtml } from "@/lib/keep-server-html";
import { reportBlankedPage } from "@/lib/blanked-page-report";

// Reload once when a script chunk fails to load (production only). Installed
// before hydration, which loads the client components.
const isReloading = import.meta.env.DEV ? () => false : reloadOnPreloadError(window);

// Put the server's HTML back if an error no component catches unmounts the
// page, as a cut-off React payload did to long transcripts. Taken before
// hydration, which may replace these nodes.
const hydrationRoot = document.getElementById("hydrate-root");
const restoreServerHtml = hydrationRoot ? keepServerHtml(hydrationRoot) : () => {};

initClient({
  hydrateRootOptions: {
    onUncaughtError: (error, errorInfo) => {
      // Logged as rwsdk's own handler, which this one replaces, logs it.
      console.error("Uncaught error: %O\n\nComponent stack:%s", error, errorInfo.componentStack);
      // React empties the root as it unmounts; check again once it is done.
      // First, so that nothing below can keep the page blank.
      restoreServerHtml();
      setTimeout(restoreServerHtml);
      // Replacing React's default handler kept these from Sentry's global
      // one; a sample goes there, tagged page_blanked. React reports after
      // this module has run, so Sentry.init below has too. Sentry's capture
      // can throw (an error whose cause chain ends in a string); React then
      // rethrows that to the window, after the HTML is back.
      reportBlankedPage(error, errorInfo);
    },
  },
});

// Initialize Sentry error tracking (production only). Errors only: the owner
// does not need Session Replay, which was a fifth of this script, so it was
// removed (2026-10-28).
if (!import.meta.env.DEV) {
  Sentry.init({
    dsn: "https://1068ac1acac045c73c8dfc7746b07df6@o4510999237558272.ingest.us.sentry.io/4510999365484544",
    // Microsoft's link scanners (Outlook Safe Links and the like) open our links
    // in an automated browser that rejects with this. Not our code (Sentry
    // WEB-CLIENT-4).
    ignoreErrors: [/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/],
    tracesSampleRate: 1.0,
    // Errors thrown by a page that is already reloading are noise.
    beforeSend: (event) => (isReloading() ? null : event),
  });
}

// Product analytics (PostHog) — production host only, and loaded once the
// page is up; see src/lib/analytics.
void initAnalytics();
